import { BCMSRecord, BCMSRecordProperty } from "@bcm-dev/bcms-vite";

/**
* @class Card
* @classdesc Class definition for record type from model
* @param {OBJECT} record - record from model to init class
* @extends BCMSRecord
* @example const record = model.Cards[0];
* const Card = new Card(record);
* @property {NUMBER} _id - number to reference card
* @property {STRING} _type - the type of card
* @property {STRING} _name - the name of card
* @property {STRING} _image - the name of the image for the card
* @property {STRING} _number - the number for the card
* @property {bcmsDate} _expDate - number representing offset for when card expires
* @property {STRING} _securityCode - 3 digit card that is usually on back for security
* @property {bcmsCurrency} _balance - the current balance on the card
* @property {bcmsCurrency} _availCredit - the amount shown for the cards credit
* @property {bcmsCurrency} _limit - limit on card
* @property {bcmsCurrency} _minPaymentAmount - minPaymentAmount on card
* @property {bcmsCurrency} _amountDue - payment amount due
* @property {bcmsDate} _dateDue - offset for when the payment is due
* @property {bcmsCurrency} _statementBalance - amount shown for the statement
* @property {bcmsDate} _lastStatementDate - offset from the last statement
* @property {NUMBER} _rewardPoints - amount of points on the card
* @property {bcmsDate} _rewardPointsExpDate - offset to when the reward points expire
* @property {bcmsCurrency} _withdrawalLimit - maximum amount allowed to withdrawal
* @property {bcmsCurrency} _purchaseLimit - maximum amount allowed for purchases
* @property {bcmsDate} _purchaseAPR - apr shown for purchases
* @property {NUMBER} _advanceAPR - apr shown for cash advances
* @property {bcmsCurrency} _membershipFee - annual fee for membership
* @property {bcmsCurrency} foreignTransactionFee - fee per foreign transactions
* @property {STRING} _active - boolean for if card is active
* @property {BOOLEAN} _owner - display name of card owner
* @property {BOOLEAN} _recentlyAdded - If the card has been added through the workflow or not
* @property {BOOLEAN} _isOn - Boolean for if the card is on or not
*/

export default class Card extends BCMSRecord {
    _id 
    _type
    _name
    _image
    _number
    _expDate
    _securityCode 
    _balance
    _availCredit
    _limit
    _minPaymentAmount
    _amountDue
    _dateDue
    _statementBalance
    _lastStatementDate
    _rewardPoints
    _rewardPointsExpDate
    _withdrawalLimit 
    _purchaseLimit
    _purchaseAPR
    _advanceAPR
    _membershipFee
    _foreignTransactionFee
    _active 
    _owner 
    _recentlyAdded
    _isOn

  constructor(record) {
    super(record)
  }
  
  /**
   * @function bySection
   * @instance
   * @returns {ARRAY}
   * @description Getter to the get the card number split by spacing into an array
   * @memberof Card
   */
  get bySection(){
    return this._number.split(" ")
  }
  
  /**
   * @function lastFour
   * @instance
   * @returns {STRING}
   * @description Getter to the get the last four numbers of the card number
   * @memberof Card
   */
  get lastFour(){
    return this._number.slice(-4)
  }

  /**
   * @function maskedNumber
   * @instance
   * @returns {STRING}
   * @description Getter to get the masked string for the card number
   * @memberof Card
   */
  get maskedNumber(){
    return "*"+this.lastFour
  }

  /**
   * @function getMaskedNumberFromNumber
   * @param {STRING} num - A 16 digit number that needs to be masked.
   * @returns {STRING}
   * @description Returns a masked 16 digit number using the 16 digit number passed as an argument.
   * @memberof Card
   */
  static getMaskedNumberFromNumber(num) {
    return "*" + num.slice(-4);
  }
}



Card.define({
  "recordType": "card",
  "className": "Card",
  "recordIDProp": "id",
  "recordProperties": [
     
   
   /** 
    * @name id
    * @instance
    * @property get {STRING} - gets the id of the Card
    * @property set {STRING} - sets the id of the Card
    * @memberof Card 
    */

    BCMSRecordProperty.mapToString("id", "_id"),
   /** 
     * @name name
     * @instance
     * @property get {STRING} - gets the name of the Card
     * @property set {STRING} - sets the name of the Card
     * @memberof Card 
     */

    BCMSRecordProperty.mapToString("name", "_name"),
    /** 
     * @name type
     * @instance
     * @property get {STRING} - gets the type of the Card
     * @property set {STRING} - sets the type of the Card
     * @memberof Card 
     */

    BCMSRecordProperty.mapToString("type", "_type"),
   
    /** 
     * @name image
     * @instance
     * @property get {STRING} - gets the image of the Card
     * @property set {STRING} - sets the image of the Card
     * @memberof Card 
     */

    BCMSRecordProperty.mapToString("image", "_image"),

   /** 
    * @name number
    * @instance
    * @property get {STRING} - gets the number of the Card
    * @property set {STRING} - sets the number of the Card
    * @memberof Card 
    */

    BCMSRecordProperty.mapToString("number", "_number"),
    
   /** 
    * @name expDate
    * @instance
    * @property get {bcmsDate} - gets the expDate of the Card
    * @property set {bcmsDate} - sets the expDate of the Card
    * @memberof Card 
    */

    BCMSRecordProperty.mapToDate("expDate", "_expDate"),
   
   
    /** 
     * @name securityCode 
     * @instance
     * @property get {STRING} - gets the securityCode  of the Card
     * @property set {STRING} - sets the securityCode  of the Card
     * @memberof Card 
     */

    BCMSRecordProperty.mapToString("securityCode", "_securityCode"),
      
   /** 
    * @name balance
    * @instance
    * @property get {bcmsCurrency} - gets the balance of the Card
    * @property set {bcmsCurrency} - sets the balance of the Card
    * @memberof Card 
    */   

    BCMSRecordProperty.mapToCurrency("balance", "_balance"),
      
    /** 
     * @name availCredit
     * @instance
     * @property get {bcmsCurrency} - gets the availCredit of the Card
     * @property set {bcmsCurrency} - sets the availCredit of the Card
     * @memberof Card 
     */

    BCMSRecordProperty.mapToCurrency("availCredit", "_availCredit"),
      
  /** 
    * @name limit
    * @instance
    * @property get {bcmsCurrency} - gets the limit of the Card
    * @property set {bcmsCurrency} - sets the limit of the Card
    * @memberof Card 
    */   

    BCMSRecordProperty.mapToCurrency("limit", "_limit"),

    /** 
    * @name minPaymentAmount
    * @instance
    * @property get {bcmsCurrency} - gets the minPaymentAmount of the Card
    * @property set {bcmsCurrency} - sets the minPaymentAmount of the Card
    * @memberof Card 
    */   
    BCMSRecordProperty.mapToCurrency("minPaymentAmount", "_minPaymentAmount"),
  
   /** 
    * @name amountDue
    * @instance
    * @property get {bcmsCurrency} - gets the amountDue of the Card
    * @property set {bcmsCurrency} - sets the amountDue of the Card
    * @memberof Card 
    */

    BCMSRecordProperty.mapToCurrency("amountDue", "_amountDue"),
        
   /** 
    * @name dateDue
    * @instance
    * @property get {bcmsDate} - gets the dateDue of the Card
    * @property set {bcmsDate} - sets the dateDue of the Card
    * @memberof Card 
    */

    BCMSRecordProperty.mapToDate("dateDue", "_dateDue"),
    
     /** 
    * @name statementBalance
    * @instance
    * @property get {bcmsCurrency} - gets the statementBalance of the Card
    * @property set {bcmsCurrency} - sets the statementBalance of the Card
    * @memberof Card 
    */

      BCMSRecordProperty.mapToCurrency("statementBalance", "_statementBalance"),
    
      /** 
       * @name lastStatementDate
       * @instance
       * @property get {bcmsDate} - gets the lastStatementDate of the Card
       * @property set {bcmsDate} - sets the lastStatementDate of the Card
       * @memberof Card 
       */
  
      BCMSRecordProperty.mapToDate("lastStatementDate", "_lastStatementDate"),
  
      /** 
       * @name rewardPoints
       * @instance
       * @property get {STRING} - gets the rewardPoints of the Card
       * @property set {STRING} - sets the rewardPoints of the Card
       * @memberof Card 
       */
  
      BCMSRecordProperty.mapToString("rewardPoints", "_rewardPoints"),
    
     /** 
      * @name rewardPointsExpDate
      * @instance
      * @property get {bcmsDate} - gets the rewardPointsExpDate of the Card
      * @property set {bcmsDate} - sets the rewardPointsExpDate of the Card
      * @memberof Card 
      */
  
      BCMSRecordProperty.mapToDate("rewardPointsExpDate", "_rewardPointsExpDate"),
  
   
     /** 
      * @name withdrawalLimit 
      * @instance
      * @property get {bcmsCurrency} - gets the withdrawalLimit  of the Card
      * @property set {bcmsCurrency} - sets the withdrawalLimit  of the Card
      * @memberof Card 
      */
  
      BCMSRecordProperty.mapToCurrency("withdrawalLimit ", "_withdrawalLimit"),
      
      /** 
       * @name purchaseLimit
       * @instance
       * @property get {bcmsCurrency} - gets the purchaseLimit of the Card
       * @property set {bcmsCurrency} - sets the purchaseLimit of the Card
       * @memberof Card 
       */
  
      BCMSRecordProperty.mapToCurrency("purchaseLimit", "_purchaseLimit"),
    
     /** 
      * @name purchaseAPR
      * @instance
      * @property get {STRING} - gets the purchaseAPR of the Card
      * @property set {STRING} - sets the purchaseAPR of the Card
      * @memberof Card 
      */   
  
      BCMSRecordProperty.mapToString("purchaseAPR", "_purchaseAPR"),

    
      /** 
       * @name advanceAPR
       * @instance
       * @property get {STRING} - gets the advanceAPR of the Card
       * @property set {STRING} - sets the advanceAPR of the Card
       * @memberof Card 
       */
  
      BCMSRecordProperty.mapToString("advanceAPR", "_advanceAPR"),
   
    /** 
      * @name membershipFee
      * @instance
      * @property get {bcmsCurrency} - gets the membershipFee of the Card
      * @property set {bcmsCurrency} - sets the membershipFee of the Card
      * @memberof Card 
      */   
  
      BCMSRecordProperty.mapToCurrency("membershipFee", "_membershipFee"),
      
     /** 
      * @name foreignTransactionFee
      * @instance
      * @property get {bcmsCurrency} - gets the foreignTransactionFee of the Card
      * @property set {bcmsCurrency} - sets the foreignTransactionFee of the Card
      * @memberof Card 
      */
  
      BCMSRecordProperty.mapToCurrency("foreignTransactionFee", "_foreignTransactionFee"),
   
     /** 
      * @name active
      * @instance
      * @property get {boolean} - gets the active of the Card
      * @property set {boolean} - sets the active of the Card
      * @memberof Card 
      */
  
      BCMSRecordProperty.mapToBoolean("active", "_active"),
  
       /** 
        * @name owner
        * @instance
        * @property get {bcmsDate} - gets the owner of the Card
        * @property set {bcmsDate} - sets the owner of the Card
        * @memberof Card 
        */
    
        BCMSRecordProperty.mapToString("owner", "_owner"),

      /**
       * @name recentlyAdded
       * @instance
       * @property get {BOOLEAN} - gets the recentlyAdded of the Card
       * @property set {BOOLEAN} - sets the recentlyAdded of the Card
       * @memberof Card
       */
      BCMSRecordProperty.mapToBoolean("recentlyAdded", "_recentlyAdded"),

      /**
       * @name isOn
       * @instance
       * @property get {BOOLEAN} - gets the isOn of the card
       * @property set {BOOLEAN} - sets the isOn of the card
       * @memberof Card
       */
      BCMSRecordProperty.mapToBoolean("isOn", "_isOn")
  ]
})
